/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import get from 'lodash.get';
import isNull from 'lodash.isnull';
import isString from 'lodash.isstring';
import {
  getAssetState,
  getBattery
} from '../entities/Asset';
import {
  AssetType,
  BaseAsset,
  CVSAssetType,
  EntityStatus,
  GenericObject,
  SCDeviceType,
  TagReportsType
} from '../types/entities';
import {
  RssiType,
  ScDeviceFeaturesResponse
} from '../types/services';
import {
  DateType
} from '../utils/DateUtil';
import {
  safeJSONParse
} from '../utils/JSONUtils';
import {
  safeParseNumber
} from '../utils/NumberUtil';
import {
  isObjectEmpty
} from '../utils/ObjectUtil';

interface GetDeviceAttribuiteProps {
  deviceAttribute?: string;
  cvsAttribute: any;
  isCVSAttribute?: boolean;
}

type NullableObject = GenericObject | null;

/*
 * Defines time zone indication, This utility method will not be necessary
 * once the services response includes the timezone in datetime values.
 */
const timezone = 'Z';

/**
 * Defines a utility to verify the datetime includes the timezone,
 * if it doesn't, it will add the 'Z' at the end of string.
 *
 * Note: This utility method will not be necessary once the services response
 * includes the timezone in datetime values.
 *
 */
const includeTimezone = (dateTime: DateType) => {
  if (isString(dateTime)) {
    return !dateTime.includes(timezone) ? `${dateTime}${timezone}` : dateTime;
  }
  return dateTime;
};

/**
 * A CVSAsset/SCDevice utility class.
 * This will provide functions to transform SC Device response to SCDeviceType.
 * Retrieve values from asset or device without having to explicitly typecast in the component.
 * Provides a method to typecast based on isScDevice as either CVSAssetType or SCDeviceType.
 *
 * ```typescript
 * import Device from '@ion/core/src/services/Device';
 *
 * const associatedAssets = safeJSONParse(Device.isScDevice(asset)?.assets);
 * ```
 */
export default class Device {
  private _device: BaseAsset = null;
  private _name: string = null; // asset name or device name or name
  private _id: number = -1; // assetId or deviceId or id
  private _assets = null;
  private _devices = null;
  private _alias: string | null = null;
  private _accountId: number = -1;
  private _assetName: string = null;
  private _accountName: string = null;
  private _accountStatus: string = null;
  private _objectId: number = -1;
  private _status: EntityStatus | string | null = null;
  private _tagReports: TagReportsType;
  private _assetType: AssetType | null = null;
  private _address: NullableObject = null; // {city, state, street, crossstreet, county, country, postalcode}
  private _dbWriteTime: number | string | null = null;
  private _eventTime: number | string | null = null;
  private _esn: string | null = null;
  private _landmark: GenericObject;
  private _landmarkId: unknown;
  private _latitude: number | string;
  private _location: unknown; // Geo-fence landmark
  private _longitude: number | string;
  private _messageUuid: string;
  private _rssi: RssiType | number;
  private _accumBatteryLevel: NullableObject = null;
  private _accumTemperature: NullableObject = null;
  private _accumLightLevel: NullableObject = null;
  private _eventType: number;
  private _postedSpeedLimit: number;
  private _shockEvent: NullableObject = null;
  private _tiltEvent: NullableObject = null;
  private _batteryLevelUnits: any = null;
  private _batteryLevelValue: any = null;
  private _isGpsValid: boolean;
  private _geometry: {
    coordinates?: Array<number>;
    x: number;
    y: number;
  };

  constructor(device) {
    this._device = device;
    // Associated assets api call getAssociatedAssets() produces assets with name instead of assetName
    this._name              = Device.getName(device);
    this._id                = Device.getId(device);
    this._assets            = device?.assets;
    this._devices           = device?.devices;
    this._assetName         = device?.assetName;
    this._alias             = device?.assetAlias;
    this._accountId         = device?.accountId;
    this._accountName       = device?.accountName;
    this._objectId          = device?.OBJECTID;
    this._status            = device?.isScDevice ? device?.status : device?.assetStatus;
    this._tagReports        = device?.isScDevice && device?.tagReports;
    this._assetType         = device?.assetType;
    this._address           = {
      city        : device?.city,
      state       : device?.state,
      street      : device?.street,
      crossStreet : device?.crossStreet,
      country     : device?.country,
      county      : device?.county,
      postalCode  : device?.postalCode
    };
    this._postedSpeedLimit  = device?.postedSpeedLimit;
    this._dbWriteTime       = device?.dbWriteTime;
    this._esn               = device?.device?.esn ?? device?.deviceEsn;
    this._landmark          = device?.landmark;
    this._landmarkId        = device?.landmarkId;
    this._latitude          = device?.latitude;
    this._longitude         = device?.longitude;
    this._location          = device?.location;
    this._messageUuid       = device?.messageUuid;
    this._rssi              = device?.rssi;
    this._geometry          = device?.geometry;
    this._accumBatteryLevel = device?.accumBatteryLevel;
    this._accumTemperature  = device?.accumTemperature;
    this._accumLightLevel   = device?.accumLightLevel;
    this._eventTime         = device?.eventTime;
    this._eventType         = device?.eventType;
    this._tiltEvent         = device?.tiltEvent;
    this._shockEvent        = device?.shockEvent;
    this._batteryLevelValue = device?.batteryLevelValue;
    this._batteryLevelUnits = device?.batteryLevelUnits;
    this._isGpsValid         = device?.gpsValidity === 'true';
  }

  public getItemValue({
    deviceAttribute,
    cvsAttribute,
    isCVSAttribute = true
  }: GetDeviceAttribuiteProps) {
    if (this._device.isScDevice && deviceAttribute) {
      const device = this._device as SCDeviceType;
      return get(device, deviceAttribute);
    } else {
      const asset = this._device as CVSAssetType;
      return isCVSAttribute ? get(asset, cvsAttribute) : cvsAttribute;
    }
  }

  public getCommonDeviceAttribute(attribute: string) {
    return get(this._device, attribute);
  }

  public getAssetValue(attribute: string) {
    const asset = this._device as CVSAssetType;
    return get(asset, attribute);
  }

  public getScValue(attribute: string) {
    const scDevice = this._device as SCDeviceType;
    return get(scDevice, attribute);
  }

  public getAssetName() {
    return this._assetName;
  }

  public isScDevice() {
    return this._device.isScDevice;
  }

  public isCVSAsset() {
    return this._device.isCvsAsset || !this._device.isScDevice;
  }

  public getDeviceStatus() {
    if (this._device.isScDevice) {
      return this._device.moving;
    }
    return getAssetState(this._device);
  }

  public getDevice() {
    return this._device;
  }

  public name() {
    return this._name;
  }

  public id(): number {
    return this._id;
  }

  public assets() {
    return this._assets;
  }

  public devices() {
    return this._devices;
  }

  public alias() {
    return this._alias;
  }

  public accountId() {
    return this._accountId;
  }

  public accountName() {
    return this._accountName;
  }

  public accountStatus() {
    return this._accountStatus;
  }

  public OBJECTID() {
    return this._objectId;
  }

  public status(): any {
    return this._status;
  }

  public tagReports() {
    return this._tagReports;
  }

  public assetType() {
    return this._assetType;
  }

  public address(): NullableObject {
    return this._address;
  }

  public dbWriteTime() {
    return this._dbWriteTime;
  }

  public eventTime() {
    return this._eventTime;
  }

  public esn() {
    return this._esn;
  }

  public landmark() {
    return this._landmark;
  }

  public landmarkId() {
    return this._landmarkId;
  }

  public latitude() {
    return this._latitude;
  }

  public location() {
    return this._location;
  }

  public longitude() {
    return this._longitude;
  }

  public messageUuid() {
    return this._messageUuid;
  }

  public rssi() {
    return this._rssi;
  }

  public geometry() {
    return this._geometry;
  }

  public accumBatteryLevel(): NullableObject {
    return this._accumBatteryLevel;
  }

  public accumTemperature(): NullableObject {
    return this._accumTemperature;
  }

  public accumLightLevel(): NullableObject {
    return this._accumLightLevel;
  }

  public eventType() {
    return this._eventType;
  }

  public postedSpeedLimit() {
    return this._postedSpeedLimit;
  }

  public tiltEvent(): NullableObject {
    return this._tiltEvent;
  }

  public shockEvent(): NullableObject {
    return this._shockEvent;
  }

  public batteryLevelValue() {
    return this._batteryLevelValue;
  }

  public batteryLevelUnits() {
    return this._batteryLevelUnits;
  }

  public valueOf(key: string) {
    return get(this._device, key);
  }

  public getTagsEsns() {
    return this._tagReports.map(tagReport => tagReport?.serialNumber);
  }

  public isGpsValid() {
    return this._isGpsValid;
  }

  public static getIndexInList(list: Device[], assetId: number, deviceObjectId: number): number {
    return list?.findIndex((item: Device) => {
      return item.id() === assetId;
    });
  }

  public static getAssetAtrribute(asset: any, attribute: string) {
    if (asset instanceof Device) {
      return asset.getAssetValue(attribute);
    }
    return get(asset, attribute);
  }

  public static getName(device) {
    if (device?.isScDevice) {
      return device?.deviceName;
    }
    return device?.assetName ?? device?.name;
  }

  public static getId(device) {
    if (device?.isScDevice) {
      return safeParseNumber(device?.id);
    }
    return device?.assetId
      ? safeParseNumber(device?.assetId)
      : safeParseNumber(device?.id);
  }

  private static _convertToAssetDevice(item, fromScLayer): Device {
    if (!isObjectEmpty(item)) {
      return new Device({
        ...item,
        batteryLevelValue : getBattery(item),
        isSciDevice       : fromScLayer,
        isCvsAsset        : !fromScLayer
      });
    }
    return null;
  }

  public static getDevicesFromCollection(collections: ScDeviceFeaturesResponse[], isParsed?: boolean): Device[] {
    if (collections.length) {
      const items: Device[] = collections
        .map((item: ScDeviceFeaturesResponse) => {
          return isParsed ? new Device(item) : Device._convertToScDevice(item);
        });
      return items;
    }
    return [];
  }

  public static getAssetsFromCollection(assets, fromScLayer = false): Device[] {
    if (assets.length) {
      const items: Device[] = assets
        .map(item => Device._convertToAssetDevice(item, fromScLayer));
      return items;
    }
    return [];
  }

  public static getDeviceFromLayer(item): Device[] {
    return [new Device({...item, isScDevice: true, isCvsAsset: false})];
  }

  public static getAssetsFromDevices(assets: Device[]): CVSAssetType[] {
    if (assets.length) {
      return assets.map((asset: Device) => asset.getDevice() as CVSAssetType);
    }
    return [];
  }

  private static _convertToScDevice(item: ScDeviceFeaturesResponse): Device {
    try {
      const {attributes, geometry} = item;
      const {
        message_type
      } = attributes;
      const accumBatteryVoltage = safeJSONParse(attributes.accum_battery_voltage);
      const accumBatteryState   = safeJSONParse(attributes.accum_battery_state);
      const accumBatteryLevel   = safeJSONParse(attributes.accum_battery_level);
      const accumLightLevel     = safeJSONParse(attributes.accum_light_level);
      const accumTemperature    = safeJSONParse(attributes.accum_temperature);
      const wirelessModem       = safeJSONParse(attributes.wireless_modem);
      const location            = safeJSONParse(attributes.location);
      const landmark            = safeJSONParse(attributes.landmark);
      const payload             = safeJSONParse(attributes.payload);
      const rssi                = safeJSONParse(attributes.rssi);
      const sensors             = safeJSONParse(attributes.sensors);
      const mobility            = sensors?.[0]?.mobility;
      const temperature         = sensors?.[0]?.temperature;
      const battery             = sensors?.[0]?.batteryLevel;
      const shock               = sensors?.[0]?.shock;
      const tilt                = sensors?.[0]?.tilt;
      const light               = sensors?.[0]?.light;
      const {computedFixStatus, fixStatus, latitude, longitude} = safeJSONParse(attributes.gps);
      const {
        city,
        country,
        county,
        crossStreet,
        postalCode,
        state,
        street,
        postedSpeedLimit
      } = location || {};
      const device = {
        id                        : attributes.device_id ?? attributes.deviceId,
        assetId                   : attributes.asset_id,
        name                      : attributes.asset_name,
        assetAlias                : attributes.asset_alias,
        accountId                 : attributes.account_id,
        accountName               : attributes.account_name,
        accountStatus             : attributes.account_status,
        assetName                 : attributes.asset_name,
        assetCategory             : attributes.asset_category,
        assetStatus               : attributes.asset_status,
        assetType                 : attributes.asset_type,
        assets                    : attributes.assets,
        created                   : includeTimezone(attributes.created),
        dbWriteTime               : includeTimezone(attributes.db_write_time),
        deviceIdType              : attributes.device_id_type,
        deviceIdValue             : attributes.device_id_value,
        deviceId                  : attributes.device_id,
        deviceAirId               : attributes.device_air_id,
        deviceName                : attributes.device_name,
        deviceType                : attributes.device_type,
        eventTime                 : includeTimezone(attributes.message_time),
        messageUuid               : attributes.message_uuid,
        OBJECTID                  : attributes.OBJECTID,
        deviceObjectId            : attributes.OBJECTID,
        messageTypeId             : attributes.message_type_id,
        messageSequenceNumber     : attributes.message_sequence_number,
        messageReceivedTime       : includeTimezone(attributes.message_received_time),
        operatorNames             : attributes.operator_name,
        pipelineRxTime            : includeTimezone(attributes.pipeline_rx_time),
        reportingDeviceId         : attributes.reporting_device_id,
        reportingDeviceStatus     : attributes.reporting_device_status,
        reportingDeviceName       : attributes.reporting_device_name,
        batteryLevelUnits         : accumBatteryLevel?.units ?? '%',
        batteryLevelValue         : battery?.value ?? accumBatteryLevel?.value,
        temperatureLevelUnits     : temperature?.units ?? accumTemperature?.units,
        temperatureLevelValue     : temperature?.value ?? accumTemperature?.value,
        temperatureBreach         : temperature?.breach,
        maxTemperatureThreshold   : temperature?.max,
        minTemperatureThreshold   : temperature?.min,
        lightLevelUnits           : light?.units ?? accumLightLevel?.units ?? 'lm',
        lightLevelValue           : light?.value ?? accumLightLevel?.value,
        lightBreach               : light?.breach,
        minLightThreshold         : light?.min,
        maxLightThreshold         : light?.max,
        postedSpeedLimit,
        commStateAvailable        : wirelessModem?.available,
        commStateConnected        : wirelessModem?.connected,
        commStateDataService      : wirelessModem?.dataService,
        commStateNetworkService   : wirelessModem?.networkService,
        commStateRoaming          : wirelessModem?.roaming,
        commStateThreeGNetwork    : wirelessModem?.carrier,
        commStateVoiceCallActive  : wirelessModem?.voiceCallIsActive,
        gps                       : !isNull(fixStatus) ? fixStatus : computedFixStatus,
        landmarkId                : landmark?.[0]?.Id,
        location                  : landmark?.[0]?.Name,
        status                    : payload?.states?.moving,
        eventType                 : payload?.status?.wakeUpType,
        tiltEvent                 : tilt?.value ?? payload?.event?.angle,
        tiltBreach                : tilt?.breach,
        shockEvent                : shock?.value ?? payload?.event?.g,
        shockBreach               : shock?.breach,
        tagReports                : payload?.tagReports,
        messageType               : message_type,
        stationary                : mobility?.duration,
        stationaryState           : mobility?.state,
        stationaryStateChangeTime : mobility?.stateChangeTime,
        isScDevice                : (
          message_type === 'LOCO_EVENT' ||
          message_type === 'TAG_REPORT'
        ),
        device : {
          id     : attributes.device_id_value,
          idType : attributes.device_id_type,
          title  : attributes.asset_type,
          esn    : attributes.device_esn
        },
        city,
        country,
        county,
        crossStreet,
        postalCode,
        state,
        street,
        landmark,
        geometry,
        latitude,
        longitude,
        payload,
        wirelessModem,
        accumBatteryVoltage,
        accumBatteryState,
        accumBatteryLevel,
        accumLightLevel,
        accumTemperature,
        sensors,
        rssi
      };
      return new Device(device);
    } catch (e) {
      return null;
    }
  }
}
